import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import GlobalHeader from '../components/GlobalHeader';
import SignatureCard from '../components/signatures/SignatureCard';
import SignatureTable from '../components/signatures/SignatureTable';
import SignatureListToolbar from '../components/signatures/SignatureListToolbar';
import ConfirmDialog from '../components/signatures/ConfirmDialog';
import VersionHistoryModal from '../components/signatures/VersionHistoryModal';
import SignaturePreviewModal from '../components/signatures/SignaturePreviewModal';
import PublicTemplatePickerModal from '../components/PublicTemplatePickerModal';
import './SignatureListPage.css';

function SignatureListPage() {
    const navigate = useNavigate();
    const { canCreate, canUpdate, canDelete } = usePermissions();
    const [signatures, setSignatures] = useState([]);
    const [filteredSignatures, setFilteredSignatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('date');
    const [selectedIds, setSelectedIds] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [signatureToDelete, setSignatureToDelete] = useState(null); // Pour suppression individuelle
    const [viewMode, setViewMode] = useState(() => {
        // Charger la préférence depuis localStorage
        return localStorage.getItem('signature_view_mode') || 'grid';
    });
    const [showVersionHistory, setShowVersionHistory] = useState(false);
    const [selectedSignatureForHistory, setSelectedSignatureForHistory] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [selectedSignatureForPreview, setSelectedSignatureForPreview] = useState(null);
    const [pendingChangesMap, setPendingChangesMap] = useState({}); // Map de signature_id -> count
    const [showTemplatePickerModal, setShowTemplatePickerModal] = useState(false);

    // Load signatures on mount
    useEffect(() => {
        loadSignatures();
    }, []);

    // Apply search and sort when signatures or filters change
    useEffect(() => {
        applyFiltersAndSort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signatures, searchTerm, sortBy]);

    const loadSignatures = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await API.getSignatures();
            const sigs = response.data.signatures || [];
            setSignatures(sigs);

            // Charger les modifications planifiées en attente
            await loadPendingChanges();
        } catch (err) {
            console.error('Failed to load signatures:', err);
            setError('Erreur lors du chargement des signatures');
        } finally {
            setLoading(false);
        }
    };

    const loadPendingChanges = async () => {
        try {
            const response = await API.getScheduledChanges({
                status: 'pending',
                entity_type: 'signature'
            });
            const changesData = response.scheduled_changes || response.data?.scheduled_changes || response.data || [];
            const changes = Array.isArray(changesData) ? changesData : [];

            // Créer une map signature_id -> count
            const map = {};
            changes.forEach(change => {
                const sigId = change.entity_id;
                map[sigId] = (map[sigId] || 0) + 1;
            });

            setPendingChangesMap(map);
        } catch (err) {
            console.error('Failed to load pending changes:', err);
            // Ne pas bloquer l'affichage si les modifications planifiées ne chargent pas
            setPendingChangesMap({});
        }
    };

    const applyFiltersAndSort = () => {
        let filtered = [...signatures];

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(sig =>
                sig.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply sort
        switch (sortBy) {
            case 'date':
                filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                break;
            case 'name':
                filtered.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'default':
                filtered.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
                break;
            default:
                break;
        }

        setFilteredSignatures(filtered);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const handleSort = (type) => {
        setSortBy(type);
    };

    const handleSelectSignature = (id) => {
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(i => i !== id)
                : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (selectedIds.length === filteredSignatures.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredSignatures.map(sig => sig.id));
        }
    };

    const handleBulkDelete = () => {
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);

            // Suppression individuelle
            if (signatureToDelete) {
                await API.deleteSignature(signatureToDelete.id);
                setMessage('Signature supprimée');
                setSignatureToDelete(null);
            }
            // Suppression en masse
            else if (selectedIds.length > 0) {
                await API.bulkDeleteSignatures(selectedIds);
                setMessage(`${selectedIds.length} signature(s) supprimée(s)`);
                setSelectedIds([]);
            }

            setShowDeleteConfirm(false);
            await loadSignatures();
        } catch (err) {
            console.error('Delete error:', err);
            setError('Erreur lors de la suppression');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setSignatureToDelete(null);
    };

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        localStorage.setItem('signature_view_mode', mode);
    };

    const handleSetDefault = async (id) => {
        try {
            await API.setDefaultSignature(id);
            setMessage('Signature définie par défaut');
            // Update locally
            setSignatures(prev => prev.map(sig => ({
                ...sig,
                is_default: sig.id === id
            })));
        } catch (err) {
            console.error('Set default error:', err);
            setError('Erreur lors de la mise à jour');
        }
    };

    const handleDuplicate = async (id) => {
        try {
            setLoading(true);
            const response = await API.duplicateSignature(id);
            setMessage(`Signature "${response.data.signature.name}" créée`);
            await loadSignatures();
        } catch (err) {
            console.error('Duplicate error:', err);
            setError('Erreur lors de la duplication');
        } finally {
            setLoading(false);
        }
    };

    const handleVersionRestore = async () => {
        try {
            setMessage('Version restaurée avec succès');
            await loadSignatures();
        } catch (err) {
            console.error('Version restore callback error:', err);
            setError('Erreur lors de la restauration');
        }
    };

    const handleEdit = (id) => {
        navigate(`/builder?id=${id}`);
    };

    const handleDelete = (id) => {
        // Trouver la signature pour afficher son nom dans la modal
        const signature = signatures.find(sig => sig.id === id);
        if (signature) {
            setSignatureToDelete(signature);
            setShowDeleteConfirm(true);
        }
    };

    const handleToggleActive = async (id) => {
        try {
            const response = await API.toggleActiveSignature(id);
            setMessage(response.data.message);
            // Mettre à jour localement
            setSignatures(prev => prev.map(sig =>
                sig.id === id ? { ...sig, is_active: response.data.is_active } : sig
            ));
        } catch (err) {
            console.error('Toggle active error:', err);
            setError('Erreur lors de la mise à jour');
        }
    };

    const handleCreateNew = () => {
        setShowTemplatePickerModal(true);
    };

    const handleSelectPublicTemplate = (templateData) => {
        setShowTemplatePickerModal(false);
        // Naviguer vers le builder avec les données du template
        navigate('/builder', { state: { initialGrid: templateData.grid_data, initialWidth: templateData.container_width } });
    };

    const handleStartEmpty = () => {
        setShowTemplatePickerModal(false);
        navigate('/builder');
    };

    const handleHistory = (id, name) => {
        const signature = signatures.find(sig => sig.id === id);
        if (signature) {
            setSelectedSignatureForHistory(signature);
            setShowVersionHistory(true);
        }
    };

    const handlePreview = async (id) => {
        try {
            // Load full signature with grid_data
            const response = await API.getSignature(id);
            if (response.data && response.data.signature) {
                setSelectedSignatureForPreview(response.data.signature);
                setShowPreview(true);
            }
        } catch (err) {
            console.error('Preview error:', err);
            setError('Erreur lors du chargement de l\'aperçu');
        }
    };

    // Auto-hide messages
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => setMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    return (
        <div className="signature-list-page">
            <GlobalHeader />

            <div className="list-container">
                <div className="list-header">
                    <h1>Mes Signatures</h1>
                    <div className="header-actions">
                        <button className="btn-templates" onClick={() => navigate('/templates')}>
                            <svg
                                width="20"
                                height="20"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                />
                            </svg>
                            Modèles
                        </button>
                        {canCreate && (
                            <button className="btn-create-new" onClick={handleCreateNew}>
                                <svg
                                    width="20"
                                    height="20"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 4v16m8-8H4"
                                    />
                                </svg>
                                Nouvelle Signature
                            </button>
                        )}
                    </div>
                </div>

                {message && (
                    <div className="message success-message">
                        {message}
                    </div>
                )}

                {error && (
                    <div className="message error-message">
                        {error}
                    </div>
                )}

                {loading && signatures.length === 0 ? (
                    <div className="loading-container">
                        <div className="spinner-large"></div>
                        <p>Chargement des signatures...</p>
                    </div>
                ) : signatures.length === 0 ? (
                    <div className="empty-state">
                        <svg
                            width="64"
                            height="64"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                        </svg>
                        <h2>Aucune signature créée</h2>
                        {canCreate ? (
                            <>
                                <p>Commencez par créer votre première signature</p>
                                <button className="btn-primary-large" onClick={handleCreateNew}>
                                    Créer ma première signature
                                </button>
                            </>
                        ) : (
                            <p>Aucune signature disponible pour le moment</p>
                        )}
                    </div>
                ) : (
                    <>
                        <SignatureListToolbar
                            searchTerm={searchTerm}
                            onSearchChange={handleSearch}
                            sortBy={sortBy}
                            onSortChange={handleSort}
                            selectedCount={selectedIds.length}
                            totalCount={filteredSignatures.length}
                            onSelectAll={handleSelectAll}
                            onBulkDelete={handleBulkDelete}
                            viewMode={viewMode}
                            onViewModeChange={handleViewModeChange}
                        />

                        {filteredSignatures.length === 0 ? (
                            <div className="empty-state">
                                <svg
                                    width="64"
                                    height="64"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1.5}
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                </svg>
                                <h2>Aucune signature trouvée</h2>
                                <p>pour "{searchTerm}"</p>
                                <button className="btn-secondary-large" onClick={() => setSearchTerm('')}>
                                    Effacer la recherche
                                </button>
                            </div>
                        ) : viewMode === 'grid' ? (
                            <div className="signature-grid">
                                {filteredSignatures.map(signature => (
                                    <SignatureCard
                                        key={signature.id}
                                        signature={signature}
                                        isSelected={selectedIds.includes(signature.id)}
                                        onSelect={handleSelectSignature}
                                        onSetDefault={handleSetDefault}
                                        onEdit={handleEdit}
                                        onDuplicate={handleDuplicate}
                                        onDelete={handleDelete}
                                        onToggleActive={handleToggleActive}
                                        onPreview={handlePreview}
                                        pendingChangesCount={pendingChangesMap[signature.id] || 0}
                                        onVersionRestore={handleVersionRestore}
                                        canUpdate={canUpdate}
                                        canDelete={canDelete}
                                    />
                                ))}
                            </div>
                        ) : (
                            <SignatureTable
                                signatures={filteredSignatures}
                                selectedIds={selectedIds}
                                onSelectSignature={handleSelectSignature}
                                onSelectAll={handleSelectAll}
                                onSetDefault={handleSetDefault}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onDuplicate={handleDuplicate}
                                onToggleActive={handleToggleActive}
                                onHistory={handleHistory}
                                onPreview={handlePreview}
                                loading={loading}
                                pendingChangesMap={pendingChangesMap}
                                canUpdate={canUpdate}
                                canDelete={canDelete}
                            />
                        )}
                    </>
                )}
            </div>

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title={signatureToDelete ? "Supprimer la signature" : "Supprimer les signatures"}
                message={
                    signatureToDelete
                        ? `Êtes-vous sûr de vouloir supprimer la signature "${signatureToDelete.name}" ? Cette action est irréversible.`
                        : `Êtes-vous sûr de vouloir supprimer ${selectedIds.length} signature(s) ? Cette action est irréversible.`
                }
                confirmText="Supprimer"
                cancelText="Annuler"
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                danger={true}
            />

            {selectedSignatureForHistory && (
                <VersionHistoryModal
                    isOpen={showVersionHistory}
                    signatureId={selectedSignatureForHistory.id}
                    signatureName={selectedSignatureForHistory.name}
                    onClose={() => {
                        setShowVersionHistory(false);
                        setSelectedSignatureForHistory(null);
                    }}
                    onRestore={handleVersionRestore}
                    canUpdate={canUpdate}
                />
            )}

            <SignaturePreviewModal
                isOpen={showPreview}
                signature={selectedSignatureForPreview}
                onClose={() => {
                    setShowPreview(false);
                    setSelectedSignatureForPreview(null);
                }}
            />

            <PublicTemplatePickerModal
                isOpen={showTemplatePickerModal}
                onClose={() => setShowTemplatePickerModal(false)}
                onSelectTemplate={handleSelectPublicTemplate}
                onStartEmpty={handleStartEmpty}
            />
        </div>
    );
}

export default SignatureListPage;
